
import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { X, Plus, Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface SmartTagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  contentForSuggestion?: string;
  placeholder?: string;
  className?: string;
}

interface TagSuggestion {
  tag: string;
  confidence: number;
  source: 'existing' | 'ai' | 'content';
}

export const SmartTagInput: React.FC<SmartTagInputProps> = ({
  value = [],
  onChange,
  contentForSuggestion = '',
  placeholder = 'Add tags...',
  className = ''
}) => {
  const [inputValue, setInputValue] = useState('');
  const [existingTags, setExistingTags] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isGeneratingAISuggestions, setIsGeneratingAISuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadExistingTags();
  }, []);

  useEffect(() => {
    if (contentForSuggestion) {
      generateAISuggestions();
    }
  }, [contentForSuggestion]);

  const loadExistingTags = async () => {
    try {
      const { data, error } = await supabase
        .from('assistant_knowledge')
        .select('metadata');

      if (error) throw error;

      const allTags = new Set<string>();
      data?.forEach(doc => {
        if (doc.metadata?.tags) {
          doc.metadata.tags.forEach((tag: string) => allTags.add(tag));
        }
      });

      setExistingTags(Array.from(allTags));
    } catch (error) {
      console.error('Error loading existing tags:', error);
    }
  };

  const generateAISuggestions = async () => {
    if (!contentForSuggestion.trim()) return;

    setIsGeneratingAISuggestions(true);
    try {
      // Simple content-based tag extraction
      const words = contentForSuggestion.toLowerCase()
        .replace(/[^\w\s]/g, ' ')
        .split(/\s+/)
        .filter(word => word.length > 3);

      const contentTags = [
        ...new Set(words.filter(word => 
          word.includes('trading') || 
          word.includes('market') ||
          word.includes('forex') ||
          word.includes('crypto') ||
          word.includes('analysis') ||
          word.includes('strategy') ||
          word.includes('risk') ||
          word.includes('profit') ||
          word.includes('guide') ||
          word.includes('tutorial')
        ))
      ].slice(0, 5);

      const aiSuggestions: TagSuggestion[] = contentTags.map(tag => ({
        tag: tag.charAt(0).toUpperCase() + tag.slice(1),
        confidence: 0.8,
        source: 'ai'
      }));

      setSuggestions(prev => [
        ...prev.filter(s => s.source !== 'ai'),
        ...aiSuggestions
      ]);
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
    } finally {
      setIsGeneratingAISuggestions(false);
    }
  };

  const updateSuggestions = (input: string) => {
    const filtered = existingTags
      .filter(tag => 
        tag.toLowerCase().includes(input.toLowerCase()) && 
        !value.includes(tag)
      )
      .slice(0, 10)
      .map(tag => ({
        tag,
        confidence: 1.0,
        source: 'existing' as const
      }));

    setSuggestions(prev => [
      ...filtered,
      ...prev.filter(s => s.source === 'ai')
    ]);
  };

  const addTag = (tag: string) => {
    const normalizedTag = tag.trim().toLowerCase();
    if (normalizedTag && !value.map(t => t.toLowerCase()).includes(normalizedTag)) {
      onChange([...value, tag.trim()]);
    }
    setInputValue('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (newValue.trim()) {
      updateSuggestions(newValue);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Selected Tags */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-sm">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="ml-2 hover:bg-destructive/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Input with Suggestions */}
      <div className="relative">
        <Popover open={showSuggestions} onOpenChange={setShowSuggestions}>
          <PopoverTrigger asChild>
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="pr-20"
            />
          </PopoverTrigger>
          
          {/* AI Suggestions Button */}
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
            {contentForSuggestion && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={generateAISuggestions}
                disabled={isGeneratingAISuggestions}
                className="h-6 px-2"
              >
                <Sparkles className={`h-3 w-3 ${isGeneratingAISuggestions ? 'animate-pulse' : ''}`} />
              </Button>
            )}
            
            {inputValue && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => addTag(inputValue)}
                className="h-6 px-2"
              >
                <Plus className="h-3 w-3" />
              </Button>
            )}
          </div>

          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandList>
                {suggestions.length === 0 ? (
                  <CommandEmpty>No suggestions found</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {suggestions.map((suggestion) => (
                      <CommandItem
                        key={suggestion.tag}
                        onSelect={() => addTag(suggestion.tag)}
                        className="flex items-center justify-between"
                      >
                        <span>{suggestion.tag}</span>
                        <div className="flex items-center space-x-1">
                          {suggestion.source === 'ai' && (
                            <Sparkles className="h-3 w-3 text-blue-500" />
                          )}
                          <Badge variant="outline" className="text-xs">
                            {suggestion.source}
                          </Badge>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* AI Suggestions Preview */}
      {suggestions.filter(s => s.source === 'ai').length > 0 && (
        <div className="text-sm text-muted-foreground">
          <span className="flex items-center">
            <Sparkles className="h-3 w-3 mr-1" />
            AI suggested tags available
          </span>
        </div>
      )}
    </div>
  );
};
